import type { Step } from './step-types'
// Owed screen — the pure half: which bucket a row is in, what the status chip
// says, and what the filter chips at the top of the screen count and show.
//
// It lives outside src/screens/owed.ts because the screen module is all DOM:
// the arithmetic behind "To chase 4" and "Waiting 2" is the part that can be
// wrong without anything looking wrong, so it is tested on fixtures rather
// than read off a rendered page (scripts/owed-view.test.mjs).

import { chaseCountOf, chaseStage, nextChaseFrom } from './chase-message'
import type { ChaseStage } from './chase-message'
import type { Clean } from './types'
import type { BroughtAccount, OwedBrought } from './brought'

// The screen's two buckets in one word. 'send' / 'waiting' / 'call' are the
// chase ladder's own stages (chase-message.chaseStage) and only ever describe
// a row somebody marked unpaid; 'unrecorded' is the other bucket — done work
// where nobody logged whether the money arrived, which is a decision to make,
// not a chase to send.
// 'invoice': a commercial customer's work Owed lists under "Invoice to send" (0080, docs/plans/commercial/AGREED.md 9):
// the server decides it (GET /api/owed invoice_to_send), so every count, filter and list reads one rule.
export type OwedStage = ChaseStage | 'unrecorded' | 'invoice'

// The chips stay as approved: "Invoice to send" rows show under All, never under To chase (AGREED.md 9).
export type OwedFilter = 'all' | 'send' | 'waiting' | 'call' | 'unrecorded' | 'invoices'

export interface OwedBucket {
  cleans: Clean[]
  total_pence: number
  count: number
  unpriced: number
  chased: number
}

export interface OwedPayload extends OwedBucket {
  unrecorded: OwedBucket
  // Today's visits nobody has marked done yet (Owed's "Done today?"); not money owed, so never in the counts.
  today_open?: Clean[]
  // Visits marked paid since midnight on the round's clock, every phone's (GET /api/owed, 10 Oct 2026).
  paid_today?: { pence: number; count: number }
  // Money brought from Squeegee (0043), only on a round that has it (src/brought.ts).
  brought?: OwedBrought
}

export interface OwedCounts {
  all: number
  invoice: number
  // Invoices still to be paid: counted by the Owed screen from its own invoice read, so 0 here.
  invoices: number
  send: number
  waiting: number
  call: number
  unrecorded: number
}

// The chip labels, in the order they sit across the top of the screen.
export const OWED_FILTERS: { value: OwedFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'send', label: 'To chase' },
  { value: 'waiting', label: 'Waiting' },
  { value: 'call', label: 'Call' },
  { value: 'unrecorded', label: 'Not recorded' },
]

// The payment message (payment_today, Liam's words) is offered for a visit done in the last week with no message yet;
// an older one gets reminder 1 under To chase. Here, not in the screen file, so Owed and the customer page ask the
// same question (customer page redesign, 9 Oct 2026).
export const PAYMENT_MESSAGE_DAYS = 7
export function paymentMessageDue(date: string, today: string): boolean {
  return date <= today && daysOldOf(date, today) <= PAYMENT_MESSAGE_DAYS
}

// Owed's "Payment message to send" (Liam, 8 Oct 2026): done, not paid, no chase counted yet, and the payment message
// still due. Owed's New layout lists the visit under it; the customer page says the same words on its Not paid card.
export function paymentMessageWaiting(cl: Clean, today: string): boolean {
  return cl.payment_status === 'unpaid' && !invoiceToSend(cl) && chaseStage(cl, today) === 'send' && chaseCountOf(cl) === 0 && paymentMessageDue(cl.date, today)
}

// Owed's "Invoice to send": the invoice is how they are asked, never the payment message or a chase (AGREED.md 9).
export function invoiceToSend(cl: Clean): boolean {
  return (cl as Clean & { invoice_to_send?: number | null }).invoice_to_send === 1
}

export function owedStageOf(cl: Clean, today: string): OwedStage {
  // Anything that is not an explicit "they have not paid" belongs to the
  // unrecorded bucket — the server splits the two payloads on the same test.
  if (cl.payment_status !== 'unpaid') return 'unrecorded'
  if (invoiceToSend(cl)) return 'invoice'
  return chaseStage(cl, today)
}

// Whole days between a clean and today. Same arithmetic the screen used
// before this module existed, moved here so the label can be tested with it.
export function daysOldOf(iso: string, today: string): number {
  return Math.round((Date.parse(today) - Date.parse(iso)) / 86_400_000)
}

// "11 days ago" reads as time passing; "11d" reads as a code. Today is
// "today", not "0 days ago", and one day is singular.
export function daysAgoLabel(days: number): string {
  if (days <= 0) return 'today'
  return days === 1 ? '1 day ago' : `${days} days ago`
}

// Search: name, address, postcode, phone or note. Every word must appear
// somewhere, in any order.
export function matchesOwed(cl: Clean, q: string): boolean {
  if (!q) return true
  const hay = [cl.customer_name, cl.customer_address, cl.customer_postcode, cl.customer_phone, cl.note]
    .map((v) => String(v ?? '').toLowerCase())
    .join(' ')
  return q.split(/\s+/).filter(Boolean).every((word) => hay.includes(word))
}

// The counts in the chip labels come from the WHOLE payload, never the
// search: a chip that changed its number as you typed would stop being a
// picture of what is owed.
// An account brought from Squeegee (0043) on the chase ladder: its own chase fields; one with nothing to chase, only a
// direct debit still collecting, waits ("Collecting £22, don't chase").
export function accountStage(a: Pick<BroughtAccount, 'chaseable_pence' | 'chase_count' | 'chased_on'>, today: string): ChaseStage {
  if (a.chaseable_pence <= 0) return 'waiting'
  return chaseStage({ chase_count: a.chase_count, chased: a.chase_count > 0 ? 1 : 0, chased_on: a.chased_on }, today)
}

export function owedCounts(payload: OwedPayload, today: string): OwedCounts {
  const rows = payload.cleans ?? []
  const unrecorded = payload.unrecorded?.cleans?.length ?? 0
  const accounts = payload.brought?.accounts ?? []
  const of = (stage: OwedStage): number => rows.filter((cl) => owedStageOf(cl, today) === stage).length + accounts.filter((a) => accountStage(a, today) === stage).length
  return {
    all: rows.length + unrecorded + accounts.length,
    invoice: rows.filter((cl) => owedStageOf(cl, today) === 'invoice').length,
    send: of('send'),
    waiting: of('waiting'),
    call: of('call'),
    unrecorded,
    invoices: 0,
  }
}

// The chip picks the list, the search narrows it. 'Not recorded' shows the
// second bucket only; every other chip hides it (All keeps both, the chase
// list first).
export function filterOwed(
  payload: OwedPayload,
  filter: OwedFilter,
  query: string,
  today: string,
): { rows: Clean[]; unrecorded: Clean[] } {
  const q = query.trim().toLowerCase()
  const un = payload.unrecorded?.cleans ?? []
  if (filter === 'unrecorded') return { rows: [], unrecorded: un.filter((cl) => matchesOwed(cl, q)) }
  const rows = (payload.cleans ?? []).filter(
    (cl) => (filter === 'all' || owedStageOf(cl, today) === filter) && matchesOwed(cl, q),
  )
  return { rows, unrecorded: filter === 'all' ? un.filter((cl) => matchesOwed(cl, q)) : [] }
}

// What the screen says when a chip's own bucket is empty — each one names the
// thing that is not there, because "nothing here" on four different lists is
// four different pieces of good news.
export function owedEmptyText(filter: OwedFilter): string {
  if (filter === 'send') return 'Nothing to chase right now'
  if (filter === 'waiting') return 'No one is waiting on a reply'
  if (filter === 'call') return 'No one needs a call'
  if (filter === 'unrecorded') return 'Every finished clean has its payment recorded'
  if (filter === 'invoices') return 'No invoices waiting to be paid'
  return 'Nothing to chase. Every clean marked unpaid has been settled.'
}

export interface OwedChip {
  text: string
  kind: 'chased' | 'wait' | 'call' | 'unknown'
}

// One chip on the row's second line, replacing the old "Chased · unpaid"
// badge and the separate chased-on text: every row on this screen is
// unsettled, so "unpaid" carried no information, and the two together said
// the same thing twice. Never chased = no chip at all.
// shortDate is passed in (ui.shortDate) so this file stays free of the DOM
// module and the screen and the chip print dates the same way.
export function owedChip(cl: Clean, today: string, shortDate: (iso: string) => string): OwedChip | null {
  if (cl.payment_status !== 'unpaid') return { text: 'Payment not recorded', kind: 'unknown' }
  const n = chaseCountOf(cl)
  if (n === 0) return null
  const stage = chaseStage(cl, today)
  if (stage === 'waiting') {
    const from = nextChaseFrom(cl)
    const on = cl.chased_on ? shortDate(cl.chased_on) : ''
    return { text: `Chased ${on} · next from ${from ? shortDate(from) : ''}`, kind: 'wait' }
  }
  const times = n === 1 ? 'once' : n === 2 ? 'twice' : `${n} times`
  // After the last rung the date stops mattering — what to do next is call.
  if (stage === 'call') return { text: `Chased ${times}`, kind: 'call' }
  return { text: cl.chased_on ? `Chased ${times} · ${shortDate(cl.chased_on)}` : `Chased ${times}`, kind: 'chased' }
}

// Three recorded sends, then a separately recorded phone call. Wording never adds a rung.
export function owedSteps(cl: Pick<Clean, 'chased' | 'chase_count' | 'chased_on' | 'payment_status' | 'chase_called_at'>, today: string, held = ''): Step[] {
  const n = chaseCountOf(cl)
  const ended = cl.payment_status === 'paid'
  const next = Math.min(n, 3)
  const turn = held || chaseStage(cl, today) === 'waiting' ? 'them' : 'you'
  return ['Payment text', 'Reminder 1', 'Reminder 2', 'Call'].map((word, i): Step => ({
    key: i === 3 ? 'call' : `message-${i + 1}`, word,
    state: (i < 3 ? i < n : !!cl.chase_called_at) ? 'done' : !ended && i === next ? 'next' : 'unknown',
    turn,
    ...(i < 3 && i === n - 1 && cl.chased_on ? { when: cl.chased_on } : {}),
    ...(i === 3 && cl.chase_called_at ? { when: cl.chase_called_at } : {}),
    waitingFor: ended ? 'Paid in full. This step was not recorded.' : held || (i === next && turn === 'them' ? `Waiting until ${nextChaseFrom(cl) ?? 'the next reminder'}` : i === 3 ? 'Waiting for your call' : 'Waiting for you to send it'),
  }))
}
